import { Box, Skeleton, Stack, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Section } from '../../ui/Section';

export interface DetailPageSkeletonProps {
  showPicture?: boolean;
  insightCount?: number;
  headlineLayout?: 'picture-six' | 'picture-six-wide' | 'row-four' | 'six' | 'two-by-two';
  showTabs?: boolean;
  showContent?: boolean;
  loadingLabel?: string;
  sx?: SxProps<Theme>;
}

const InsightSkeleton = () => (
  <Section style={{ p: 4, mb: 0, height: '100%' }}>
    <Stack
      spacing={1}
      aria-hidden="true"
    >
      <Skeleton
        variant="text"
        width="55%"
        height={20}
      />
      <Skeleton
        variant="text"
        width="70%"
        height={36}
      />
      <Skeleton
        variant="text"
        width="85%"
        height={20}
      />
    </Stack>
  </Section>
);

const pictureSixSkeletonAreas = [
  'tile1',
  'tile2',
  'tile3',
  'tile4',
  'tile5',
  'tile6',
] as const;

export const DetailPageSkeleton = ({
  showPicture = true,
  insightCount = 6,
  headlineLayout,
  showTabs = false,
  showContent = true,
  loadingLabel,
  sx,
}: DetailPageSkeletonProps) => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const tileCount = Number.isFinite(insightCount)
    ? Math.max(0, Math.floor(insightCount))
    : 0;
  const resolvedHeadlineLayout = headlineLayout
    ?? (showPicture && tileCount === 6 ? 'picture-six' : undefined);
  const isPictureSix = resolvedHeadlineLayout === 'picture-six' || resolvedHeadlineLayout === 'picture-six-wide';
  const isPictureSixWide = resolvedHeadlineLayout === 'picture-six-wide';

  return (
    <Box
      sx={sx}
      role="status"
      aria-busy="true"
      aria-label={loadingLabel ?? t('loading')}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            width="min(70%, 420px)"
            height={48}
          />
          <Skeleton
            variant="text"
            width="min(90%, 620px)"
            height={28}
          />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Skeleton
            variant="rounded"
            width={112}
            height={40}
          />
          <Skeleton
            variant="rounded"
            width={112}
            height={40}
          />
        </Stack>
      </Box>

      {((showPicture && isPictureSix) || tileCount > 0) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isPictureSix
              ? {
                xs: '1fr',
                md: isPictureSixWide
                  ? 'minmax(0, 1.2fr) repeat(3, minmax(0, 1fr))'
                  : 'minmax(0, 1.2fr) repeat(2, minmax(0, 1fr))',
              }
              : '1fr',
            gridTemplateAreas: isPictureSix
              ? {
                xs: '"image" "tile1" "tile2" "tile3" "tile4" "tile5" "tile6"',
                md: isPictureSixWide
                  ? '"image tile1 tile2 tile3" "image tile4 tile5 tile6"'
                  : '"image tile1 tile2" "image tile3 tile4" "image tile5 tile6"',
              }
              : undefined,
            gap: 6,
            mt: 6,
            alignItems: 'stretch',
          }}
        >
          {showPicture && isPictureSix && (
            <Section
              style={{
                p: 0,
                mb: 0,
                height: '100%',
                ...(isPictureSix ? { gridArea: 'image' } : {}),
              }}
            >
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: { xs: 220, sm: 260, md: '100%' },
                  bgcolor: theme.palette.custom.gray[100],
                }}
              />
            </Section>
          )}

          {tileCount > 0 && (
            isPictureSix
              ? Array.from({ length: tileCount }, (_, index) => (
                <Box
                  key={`insight-skeleton-${index}`}
                  sx={{ gridArea: pictureSixSkeletonAreas[index], minWidth: 0 }}
                >
                  <InsightSkeleton />
                </Box>
              ))
              : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isPictureSix
                      ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                      : resolvedHeadlineLayout === 'row-four'
                        ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }
                        : resolvedHeadlineLayout === 'two-by-two'
                          ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                          : {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                          },
                    gap: 6,
                    gridColumn: isPictureSix ? { md: '2 / span 2' } : undefined,
                    minWidth: 0,
                  }}
                >
                  {Array.from({ length: tileCount }, (_, index) => (
                    <InsightSkeleton key={`insight-skeleton-${index}`} />
                  ))}
                </Box>
              )
          )}
        </Box>
      )}

      {showTabs && (
        <Box sx={{ mt: 6, px: { xs: 0, md: 4 } }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={40}
          />
        </Box>
      )}

      {showContent && (
        <Section style={{ mt: 6, p: 5, mb: 0 }}>
          <Stack
            spacing={2}
            aria-hidden="true"
          >
            <Skeleton
              variant="text"
              width="30%"
              height={28}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={220}
              sx={{ bgcolor: theme.palette.custom.gray[100] }}
            />
          </Stack>
        </Section>
      )}
    </Box>
  );
};
